import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  FileText, Upload, Database, Webhook, Key, Send,
  BarChart2, Settings, LogOut, Shield, Building2,
  Users, Sliders, ChevronLeft, ChevronRight, LayoutDashboard,
  CreditCard, Home, FlaskConical, Zap, PieChart, BookOpen,
  TrendingUp, Bell, Layers
} from "lucide-react";
import { useAppConfig } from "@/contexts/AppConfigContext";

const NavItem = ({ to, icon: Icon, label, collapsed }) => {
  const loc = useLocation();
  const active = loc.pathname === to || (to !== "/" && loc.pathname.startsWith(to));
  return (
    <Link
      to={to}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 group ${
        active
          ? "bg-[#E5F0F2] text-[#006080] border-l-2 border-[#006080] pl-[10px]"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      }`}
      title={collapsed ? label : ""}
    >
      <Icon size={16} strokeWidth={1.5} className="flex-shrink-0" />
      {!collapsed && <span>{label}</span>}
    </Link>
  );
};

const Section = ({ title, collapsed, children }) => (
  <div className="mb-2">
    {!collapsed && (
      <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 px-3 mb-1 mt-4">{title}</p>
    )}
    <div className="space-y-0.5">{children}</div>
  </div>
);

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const { appName, logoB64 } = useAppConfig();
  const navigate = useNavigate();
  const isSuperAdmin = user?.role === "superadmin";
  const isAdmin = ["superadmin", "company_admin"].includes(user?.role);
  const hasBulk = isSuperAdmin || user?.bulk_processing_enabled === true;

  const handleLogout = () => { logout(); navigate("/login"); };

  return (
    <aside
      data-testid="sidebar"
      className={`${collapsed ? "w-14" : "w-56"} flex-shrink-0 bg-[#F8F8F5] border-r border-slate-200 flex flex-col transition-all duration-200`}
    >
      {/* Logo */}
      <div className={`flex items-center ${collapsed ? "justify-center px-2" : "px-4"} h-14 border-b border-slate-200`}>
        {collapsed ? (
          logoB64 ? (
            <img src={`data:image/png;base64,${logoB64}`} alt={appName} className="w-7 h-7 object-contain" />
          ) : (
            <div className="w-7 h-7 bg-[#006080] rounded-md flex items-center justify-center">
              <FileText size={14} className="text-white" />
            </div>
          )
        ) : (
          <div className="flex items-center gap-2">
            {logoB64 ? (
              <img src={`data:image/png;base64,${logoB64}`} alt={appName} className="h-7 max-w-[120px] object-contain" />
            ) : (
              <>
                <div className="w-7 h-7 bg-[#006080] rounded-md flex items-center justify-center flex-shrink-0">
                  <FileText size={14} className="text-white" />
                </div>
                <span className="font-bold text-slate-900 text-sm">{appName}</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 p-2 overflow-y-auto">
        <Section title="Main" collapsed={collapsed}>
          <NavItem to="/dashboard" icon={Home} label="Dashboard" collapsed={collapsed} />
          <NavItem to="/documents" icon={FileText} label="Documents" collapsed={collapsed} />
          <NavItem to="/documents/upload" icon={Upload} label="Upload" collapsed={collapsed} />
        </Section>

        {isAdmin && (
          <Section title="Configuration" collapsed={collapsed}>
            <NavItem to="/schemas" icon={Database} label="Schemas" collapsed={collapsed} />
            <NavItem to="/webhooks" icon={Webhook} label="Webhooks" collapsed={collapsed} />
            <NavItem to="/api-keys" icon={Key} label="API Keys" collapsed={collapsed} />
            <NavItem to="/requests" icon={Send} label="Doc Requests" collapsed={collapsed} />
            <NavItem to="/playground" icon={FlaskConical} label="Playground" collapsed={collapsed} />
          </Section>
        )}

        {isAdmin && (
          <Section title="Pipelines" collapsed={collapsed}>
            <NavItem to="/pipelines" icon={Zap} label="Pipelines" collapsed={collapsed} />
          </Section>
        )}

        {hasBulk && isAdmin && (
          <Section title="Bulk" collapsed={collapsed}>
            <NavItem to="/bulk" icon={Layers} label="Bulk Processing" collapsed={collapsed} />
          </Section>
        )}

        <Section title="Insights" collapsed={collapsed}>
          <NavItem to="/analytics" icon={BarChart2} label="Analytics" collapsed={collapsed} />
          <NavItem to="/reports" icon={PieChart} label="Reports" collapsed={collapsed} />
        </Section>

        {isAdmin && (
          <Section title="Developer" collapsed={collapsed}>
            <NavItem to="/developer" icon={BookOpen} label="API Docs" collapsed={collapsed} />
          </Section>
        )}

        {isAdmin && (
          <Section title="Notifications" collapsed={collapsed}>
            <NavItem to="/alerts" icon={Bell} label="Alert Settings" collapsed={collapsed} />
          </Section>
        )}

        {isSuperAdmin && (
          <Section title="Billing" collapsed={collapsed}>
            <NavItem to="/admin/topup" icon={TrendingUp} label="Credit Top-Up" collapsed={collapsed} />
          </Section>
        )}

        {isSuperAdmin && (
          <Section title="Super Admin" collapsed={collapsed}>
            <NavItem to="/admin" icon={Shield} label="Dashboard" collapsed={collapsed} />
            <NavItem to="/admin/companies" icon={Building2} label="Companies" collapsed={collapsed} />
            <NavItem to="/admin/users" icon={Users} label="Users" collapsed={collapsed} />
            <NavItem to="/admin/config" icon={Sliders} label="Config" collapsed={collapsed} />
          </Section>
        )}
      </nav>

      {/* Footer */}
      <div className="p-2 border-t border-slate-200 space-y-0.5">
        <NavItem to="/profile" icon={Settings} label="Profile" collapsed={collapsed} />
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors duration-150"
          title={collapsed ? "Logout" : ""}
        >
          <LogOut size={16} strokeWidth={1.5} className="flex-shrink-0" />
          {!collapsed && <span>Logout</span>}
        </button>
        <button
          onClick={() => setCollapsed(!collapsed)}
          data-testid="sidebar-toggle"
          className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors duration-150"
        >
          {collapsed ? <ChevronRight size={16} /> : <><ChevronLeft size={16} /><span>Collapse</span></>}
        </button>
      </div>
    </aside>
  );
}
